/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/contacts/ContactDrawerBodyChrome.tsx
 * PURPOSE: Collapsible sections, dirty or error chrome (no per-section Save or Cancel — drawer footer), skeleton for ContactDrawerBody (components only — shared constants live in contactDrawerBodyChrome.shared.ts for Fast Refresh).
 * DEPENDENCIES: react, @heroicons/react/24/outline, tailwind-merge
 * PUBLIC_API: CollapsibleSection, SectionChrome, TagAddRow, InitialsChip, ContactDrawerSkeleton
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 153/250
 * RECENT: 2026-05-08 [2.2.873] — Split non-component exports to contactDrawerBodyChrome.shared.ts (ESLint react-refresh); shell CHANGELOG [2.2.873]
 */

import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export function CollapsibleSection({ title, children }: { title: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border-b border-gray-200 py-4 last:border-b-0 dark:border-gray-700">
      <button
        type="button"
        className="mb-3 flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md text-left focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${title} section` : `Expand ${title} section`}
        onClick={() => setExpanded((e) => !e)}
      >
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        )}
      </button>
      {expanded ? <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">{children}</div> : null}
    </div>
  );
}

export function SectionChrome({
  sectionId,
  dirty,
  errorText,
  children,
}: {
  sectionId: string;
  dirty: boolean;
  errorText: string | null;
  children: ReactNode;
}) {
  return (
    <section
      className={twMerge(
        'rounded-lg border border-transparent px-1 py-0',
        dirty && 'border-l-2 border-amber-400 pl-2'
      )}
      data-testid={`contact-section-wrap-${sectionId}`}
      data-dirty={dirty ? 'true' : 'false'}
    >
      {children}
      {errorText ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {errorText}
        </p>
      ) : null}
    </section>
  );
}

export function TagAddRow({ onAdd }: { onAdd: (label: string) => void }) {
  const [draft, setDraft] = useState('');
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <input
        aria-label="New tag name"
        className="block min-h-[44px] max-w-xs rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        placeholder="New tag"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdd(draft);
            setDraft('');
          }
        }}
      />
      <button
        type="button"
        className="inline-flex min-h-[44px] items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        onClick={() => {
          onAdd(draft);
          setDraft('');
        }}
      >
        Add tag
      </button>
    </div>
  );
}

export function InitialsChip({ firstName, lastName }: { firstName: string; lastName: string }) {
  const a = (firstName.trim().charAt(0) || '').toUpperCase();
  const b = (lastName.trim().charAt(0) || '').toUpperCase();
  const text = a && b ? `${a}${b}` : a || b || '?';
  return (
    <span
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-800 dark:bg-primary-900/40 dark:text-primary-200"
      aria-hidden
    >
      {text}
    </span>
  );
}

export function ContactDrawerSkeleton() {
  return (
    <div className="space-y-6" data-testid="contact-drawer-skeleton" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse space-y-3">
          <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800" />
            <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
  );
}
